/**
 * @file     DemographicsPanelClient.tsx
 * @purpose  Census figures for the listing's Statistics Canada dissemination
 *           area: a StatRow of the headline figures, a FactList of the rest,
 *           and a breakdown (income, age, education, housing, commute) as
 *           MeterRows behind a SegmentedControl. Values carry
 *           data-premium-value so the People gate can blur them alone.
 *           Falls back to one line when the profile is still ingesting.
 *           @/services/external-signals/statcanCensus
 */
'use client';

import * as React from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { reltaTrack } from '@/hooks/useReltaTrack';
import { StatRow } from '@/components/hub/StatRow';
import { FactList } from '@/components/hub/FactList';
import { MeterRow } from '@/components/hub/MeterRow';
import { SegmentedControl } from '@/components/hub/SegmentedControl';
import { hubColors, microLabelSx } from '@/components/hub/hubKitStyles';
import { usePanelImpression } from '@/hooks/usePanelImpression';
import { useAnalytics } from '@/hooks/useAnalytics';
import { analyticsEvents } from '@/core/constants/analytics';
import type { DaInfo, CensusProfile, CensusBucket } from '@/services/external-signals/statcanCensus';

interface Props {
  da: DaInfo;
  profile: CensusProfile | null;
}

type TabId = 'income' | 'age' | 'education' | 'housing' | 'commute';

const TABS: ReadonlyArray<{ value: TabId; label: string }> = [
  { value: 'income', label: 'Income' },
  { value: 'age', label: 'Age' },
  { value: 'education', label: 'Education' },
  { value: 'housing', label: 'Housing' },
  { value: 'commute', label: 'Commute' },
];

const fmtInt = (n: number | null | undefined) => (n != null ? n.toLocaleString('en-CA') : null);
const fmtFloat = (n: number | null | undefined, digits: number) => (n != null ? n.toFixed(digits) : null);
const fmtPct = (n: number | null | undefined) => (n != null ? `${Math.round(n)}%` : null);
const fmtCurrency = (n: number | null | undefined) =>
  n != null ? `$${n.toLocaleString('en-CA', { maximumFractionDigits: 0 })}` : null;

export function DemographicsPanelClient({ da, profile }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = React.useState<TabId>('income');
  const ref = React.useRef<HTMLDivElement | null>(null);
  const { trackEvent } = useAnalytics();
  usePanelImpression(ref, analyticsEvents.propertyDemographicsViewed, {
    metadata: { dauid: da.dauid, has_profile: Boolean(profile) },
  });

  const buckets: CensusBucket[] = profile?.buckets[tab] ?? [];
  const total = buckets.reduce((sum, b) => sum + b.value, 0);
  const area = [t('listingDetail.demographics.area', { defaultValue: 'Statistics Canada area {{id}}', id: da.dauid }), da.csdName]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box ref={ref} component="div" aria-label={t('listingDetail.demographics.label', { defaultValue: 'Who lives here' })}>
      <Box component="p" sx={{ ...microLabelSx, mb: 3 }}>{area}</Box>

      {profile ? (
        <>
          <Box data-premium-value="true">
            <StatRow
              items={[
                { label: t('listingDetail.demographics.medianAge', { defaultValue: 'Median age' }), value: fmtFloat(profile.medianAge, 0) },
                { label: t('listingDetail.demographics.renters', { defaultValue: 'Renter households' }), value: fmtPct(profile.rentersPct) },
                { label: t('listingDetail.demographics.householdSize', { defaultValue: 'Average household size' }), value: fmtFloat(profile.avgHouseholdSize, 1) },
                {
                  label: t('listingDetail.demographics.income', { defaultValue: 'Median household income' }),
                  value: fmtCurrency(profile.medianHouseholdIncome ?? profile.avgHouseholdIncome),
                },
              ]}
            />
          </Box>
          <Box data-premium-value="true" sx={{ mt: 4 }}>
            <FactList
              items={[
                { label: t('listingDetail.demographics.population', { defaultValue: 'Population (2021)' }), value: fmtInt(profile.population2021 ?? da.population2021) },
                { label: t('listingDetail.demographics.medianRent', { defaultValue: 'Median rent' }), value: fmtCurrency(profile.medianMonthlyRent) },
                { label: t('listingDetail.demographics.ownerCost', { defaultValue: 'Owner shelter cost' }), value: fmtCurrency(profile.medianMonthlyOwnerCost) },
                { label: t('listingDetail.demographics.shelter30', { defaultValue: '30%+ of income on shelter' }), value: fmtPct(profile.shelterCostOver30PctPct) },
                { label: t('listingDetail.demographics.owners', { defaultValue: 'Owner households' }), value: fmtPct(profile.ownersPct) },
                { label: t('listingDetail.demographics.kids', { defaultValue: 'Households with kids' }), value: fmtPct(profile.householdsWithChildrenPct) },
                { label: t('listingDetail.demographics.immigrants', { defaultValue: 'Immigrants' }), value: fmtPct(profile.immigrantsPct) },
                { label: t('listingDetail.demographics.postSecondary', { defaultValue: 'Post-secondary' }), value: fmtPct(profile.postsecondaryPct) },
                { label: t('listingDetail.demographics.homeValue', { defaultValue: 'Average home value (2021)' }), value: fmtCurrency(profile.avgDwellingValue) },
              ]}
            />
          </Box>

          <Box sx={{ mt: 6, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
            <Box component="h3" sx={{ ...microLabelSx, fontSize: 14 }}>
              {t('listingDetail.demographics.breakdown', { defaultValue: 'Breakdown' })}
            </Box>
            <SegmentedControl<TabId>
              size="sm"
              options={TABS.map((o) => ({ ...o, label: t(`listingDetail.demographics.tab.${o.value}`, { defaultValue: o.label }) }))}
              value={tab}
              onChange={(next) => {
                setTab(next);
                trackEvent({
                  eventName: analyticsEvents.propertyDemographicsTabSwitched,
                  eventType: 'click',
                  category: 'property_detail_panel',
                  label: next,
                  metadata: { dauid: da.dauid, from: tab, to: next },
                });
                reltaTrack('panel.demographics.tab_switched', { tab: next });
              }}
              ariaLabel={t('listingDetail.demographics.breakdownLabel', { defaultValue: 'Switch demographic breakdown' })}
            />
          </Box>
          <Box data-premium-value="true" sx={{ mt: 2 }}>
            {buckets.length >= 2 && total > 0 ? (
              buckets.map((b) => (
                <MeterRow
                  key={`${tab}-${b.label}`}
                  label={b.label}
                  value={b.value}
                  max={total}
                  valueText={`${((b.value / total) * 100).toFixed(1)}% · ${b.value.toLocaleString('en-CA')}`}
                  tone="primary"
                />
              ))
            ) : (
              <Box component="p" sx={{ m: 0, py: 3, fontSize: 14, color: hubColors.textSecondary }}>
                {t('listingDetail.demographics.notPublished', { defaultValue: 'Statistics Canada has not published this breakdown for this area.' })}
              </Box>
            )}
          </Box>
        </>
      ) : (
        <Box component="p" sx={{ m: 0, fontSize: 15, color: hubColors.textSecondary }}>
          {t('listingDetail.demographics.pending', { defaultValue: 'Census figures for this area are loading. Check back in a minute.' })}
        </Box>
      )}

      <Box component="p" sx={{ m: 0, mt: 4, fontSize: 13, color: hubColors.textSecondary }}>
        {t('listingDetail.demographics.source', {
          defaultValue: 'Source: Statistics Canada, 2021 Census Profile, dissemination area {{id}}.',
          id: da.dauid,
        })}
      </Box>
    </Box>
  );
}
